import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Typography, useTheme } from "@mui/material";
import { CheckRounded } from "@mui/icons-material";
import { TemporaryMessage } from "../../../../../components/temporary-message";
import { handlePromise } from "../../../../../utils/handlePromisse";
import { Patient as PatientModel, getPatientsByNameObservation } from "../../../../../services/patient.service";
import { PatientHexAvatar, SearchField, useRowToggle } from "../../../../../components/ui";

type QueryFormProps = {
    reloadPatients: boolean
    patientSelected: (ids: number[]) => void
    /** Paciente a pré-selecionar (ex.: "Nova consulta" a partir do paciente) */
    preselectId?: number
}

type PatientPickerRowProps = {
    patient: PatientModel
    isSelected: boolean
    /** Mostra a borda superior (todas as linhas menos a primeira) */
    divided: boolean
    onToggle: () => void
}

/** Linha selecionável de paciente no wizard (hexágono + nome + check). */
const PatientPickerRow: React.FC<PatientPickerRowProps> = ({ patient, isSelected, divided, onToggle }) => {
    const theme = useTheme()
    const toggleProps = useRowToggle(onToggle, { checked: isSelected, label: `Selecionar ${patient.name ?? 'paciente'}` })

    return (
        <Box
            {...toggleProps}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                px: '16px',
                py: '10px',
                cursor: 'pointer',
                borderTop: divided ? `1px solid ${theme.palette.divider}` : 'none',
                backgroundColor: isSelected ? theme.palette.brand.cream : 'background.paper',
                border: isSelected ? `1.5px solid ${theme.palette.primary.main}` : undefined,
                borderRadius: isSelected ? '13px' : undefined,
                transition: 'background-color .15s',
                '&:hover': {
                    backgroundColor: isSelected ? theme.palette.brand.cream : theme.palette.brand.surface2,
                },
                '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
            }}
        >
            <PatientHexAvatar name={patient.name ?? '?'} size={36} />
            <Typography
                sx={{
                    flex: 1,
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: isSelected ? 'primary.main' : 'text.primary',
                    lineHeight: 1.3,
                }}
            >
                {patient.name ?? '—'}
            </Typography>
            {isSelected && (
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <CheckRounded sx={{ fontSize: 14, color: 'primary.contrastText' }} />
                </Box>
            )}
        </Box>
    )
}

const QueryForm: React.FC<QueryFormProps> = ({ reloadPatients, patientSelected, preselectId }) => {
    const [selected, setSelected] = useState<PatientModel[]>([])
    const [patients, setPatients] = useState<PatientModel[]>([])
    const [search, setSearch] = useState('')
    const preselectApplied = useRef(false)
    const theme = useTheme()

    const fetchPatients = useCallback(async () => {
        const [result] = await handlePromise(getPatientsByNameObservation())
        const fetchedPatients = (Array.isArray(result.data) ? result.data : result.data.data) as PatientModel[];
        const sortedPatients = fetchedPatients.sort((a, b) => {
            if (!a.name || !b.name) return 0;
            return a.name.localeCompare(b.name);
        });
        setPatients(sortedPatients);
    }, [])

    useEffect(() => {
        fetchPatients()
    }, [fetchPatients, reloadPatients])

    // Pré-seleciona o paciente vindo da ficha (uma única vez, após carregar a lista)
    useEffect(() => {
        if (preselectApplied.current || preselectId == null || patients.length === 0) return
        const match = patients.find(p => p.id === preselectId)
        if (!match) return
        preselectApplied.current = true
        setSelected([match])
        patientSelected([match.id])
    }, [patients, preselectId, patientSelected])

    const togglePatient = (patient: PatientModel) => {
        const isSelected = selected.some(p => p.id === patient.id)
        const next = isSelected
            ? selected.filter(p => p.id !== patient.id)
            : [...selected, patient]
        setSelected(next)
        patientSelected(next.map(p => p.id))
    }

    const normalizeStr = (s: string) =>
        s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

    const filteredPatients = patients.filter(p =>
        !search || normalizeStr(p.name ?? '').includes(normalizeStr(search))
    )

    return (
        <Box>
            <SearchField
                value={search}
                onChange={setSearch}
                placeholder="Buscar paciente..."
            />

            <Box
                sx={{
                    mt: '12px',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '13px',
                    overflow: 'hidden',
                }}
            >
                {filteredPatients.length === 0 && (
                    <Typography sx={{ p: '16px', fontSize: 13.5, color: 'text.secondary', textAlign: 'center' }}>
                        Nenhum paciente encontrado.
                    </Typography>
                )}
                {filteredPatients.map((patient, index) => (
                    <PatientPickerRow
                        key={patient.id}
                        patient={patient}
                        isSelected={selected.some(p => p.id === patient.id)}
                        divided={index > 0}
                        onToggle={() => togglePatient(patient)}
                    />
                ))}
            </Box>

            <Typography sx={{ mt: '10px', fontSize: 12, color: 'text.secondary' }}>
                Você pode selecionar um ou mais pacientes. Caso não queira vincular, inicie sem selecionar.
            </Typography>

            <TemporaryMessage />
        </Box>
    )
}

export default QueryForm;
